import { ChainID } from '@jdiyield/constants'
import { Contract, PriceList, Stake, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import latteSwapABI from '../abi/latteswap.json'
import { calculate } from '../calculate'
import getNodeUrl from '../rpcRandom'
import { findToken, makeBatchRequest } from '../utils'

const lpAddress = [
  // latte
  '0x8d78c2ff1fb4fba08c7691dfeac7bb425a91c81a',
  // ibAlpaca
  '0xf1be8ecc990cbcb90e166b71e368299f0116d421',
  // busd
  '0xe9e7cea3dedca5984780bafc599bd69add087d56',
  // belt
  '0xe0e514c71282b6f4e823703a39374cf58dc3ea4f',
  // xvs
  '0xcf6bb5389c92bdda8a3747ddb454cb7a64626c63',
  // eps
  '0xA7f552078dcC247C2684336020c03648500C6d9F',
  // btcb
  '0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c',
  // eth
  '0x2170ed0880ac9a755fd29b2688956bd959f933f8',
  // usdc-usd
  '0xee4341dc1a64720c9e7ae776daebf6ec5367a9f0',
  // usdt-busd
  '0x318b894003d0eacfedaa41b8c70ed3ce1fde1450',
  // wbnb
  '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c',
  // cake
  '0x0e09fabb73bd3ade0a17ecc321fd13a19e81ce82',
  // busd-alpaca
  '0x7ffafa8f5846476688708096f6cdf7c96caa5b32',
  // eth-bnb
  '0xcff09b973f21489d76a9396b1505eab04766d58c',
  //psc latte-busd
  '0xc019ca579efa75be917f38afb1113030c06ba035',
  // btcb-busd
  '0xbd4284d34b9673fc79aab2c0080c5a19b4282425',
  // latte-eth
  '0x937f273568428d7dcacc2fb43330a70d330b641d',
  // latte-busd
  '0xb4bc3f991aec9c54b489d5a5db818487db42857d',
  // btcb-bnb
  '0x79666a68ee70c36d5f865e59afd3b2fbdad41a0c',
  // eth-btcb
  '0x3dcb13386e0a5353e0b2e5b53858be6ffb888bb5',
  // bnb-busd
  '0x1fec7039304a237a8402214488fbfb0f777c08e6'
]

export const getLatteSwap = async (
  walletAddress: string,
  contract: Contract,
  tokens: Token[],
  priceList: PriceList,
  web3Prop: Web3
): Promise<Stake[]> => {
  const { contractAddress, rewardAddress, routerAddress } = contract
  try {
    const web3 = web3Prop || new Web3(getNodeUrl(56))
    const ct = new web3.eth.Contract(latteSwapABI as any, contractAddress)

    const userInfoResult = await makeBatchRequest(
      web3,
      lpAddress.map((address) => ct.methods.userInfo(address, walletAddress).call)
    )

    const result: any = await Promise.all(
      userInfoResult.map(async (userInfo, idx) => {
        const balanceInfo = await calculate(
          web3,
          {
            lpAddress: lpAddress[idx],
            pool: 0,
            availableBalanceOf: '0',
            balance: userInfo.amount
          },
          routerAddress,
          tokens,
          priceList
        )
        const pendingReward = await ct.methods.pendingLatte(lpAddress[idx], walletAddress).call()
        const reward = new BigNumber(pendingReward as string).dividedBy(`1e18`).toNumber()
        const rewardUSD = reward * priceList[rewardAddress]?.usd || 0
        const rewardSymbol = findToken(tokens, rewardAddress)?.symbol ?? '???'

        return {
          ...contract,
          ...balanceInfo,
          reward: +reward,
          rewardSymbol,
          rewardUSD
        }
      })
    )

    return result
  } catch (e) {
    console.log('latte swap', e)
    return []
  }
}

export async function getLatteSwapAPR(
  contractAddress: string,
  lpAddress: string,
  chainID: ChainID,
  getBonusMultiplier?: string
): Promise<{
  rewardPerBlock: string
  multiplier: string
  allocPoints: string
  totalAllocPoints: string
  stakeTokenAddress: string
}> {
  const RPC_URL = getNodeUrl(chainID)
  const web3 = new Web3(RPC_URL)
  const ct = new web3.eth.Contract(latteSwapABI as any, contractAddress)
  const poolInfo = await ct.methods.poolInfo(lpAddress).call()

  const rewardPerBlock = await ct.methods.lattePerBlock().call()

  const stakeTokenAddress = lpAddress
  const allocPoints = poolInfo.allocPoint
  const totalAllocPoints =
    (await ct.methods
      .totalAllocPoint()
      .call()
      .catch(() => allocPoints)) || +allocPoints

  let multiplier = '1'
  try {
    if (getBonusMultiplier && getBonusMultiplier !== 'none')
      multiplier = await ct.methods[`${getBonusMultiplier}`]().call()
  } catch (error) {
    multiplier = '1'
  }

  return {
    rewardPerBlock,
    multiplier,
    allocPoints,
    totalAllocPoints,
    stakeTokenAddress
  }
}
